import Close from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { AdditionalServicesService } from "./additional-services-service";
import { AtsModalContent } from "./ats-modal-content";
import { DesignServicesModalContent } from "./design-services-modal-content";
import { EGiftingModalContent } from "./egifting-modal-content";
import { ExosModalContent } from "./exos-modal-content";
import { KnowledgeHubContent } from "./knowledge-hub-content";
import { ListenModalContent } from "./listen-modal-content";
import { P2pModalContent } from "./p2p-modal-content";
import { PagesModalContent } from "./pages-modal-content";
import { SmsModalContent } from "./sms-modal-content";
import { TwoWaySmsModalContent } from "./sms-two-way-modal-content";
import { SwagModalContent } from "./swag-modal-content";
import { VideographyModalContent } from "./videography-modal-content";

import type { DialogController } from "../../utils/hooks/use-dialog";
import type { AdditionalServicesType } from "./type";
import type { FC } from "react";

interface AdditionalServicesConfig {
  heading: string;
  headerImage?: string;
  images: { url: string; width?: number; height?: number }[];
  content: FC;
  minHeight?: string;
}

const AdditionalServices: Record<AdditionalServicesType, AdditionalServicesConfig> = {
  listen: {
    heading: "Listen",
    headerImage: "https://images.changeengine.com/listen-modal-2-header.webp",
    minHeight: "600px",
    images: [
      { url: "https://images.changeengine.com/listen-loop.webp" },
      { url: "https://images.changeengine.com/survey-graphic.webp", width: 210 },
    ],
    content: ListenModalContent,
  },
  swag: {
    heading: "Swag",
    headerImage: "https://images.changeengine.com/swagservice-header.webp",
    images: [
      { url: "https://images.changeengine.com/swagservice1.webp" },
      { url: "https://images.changeengine.com/swagservice3.webp" },
      { url: "https://images.changeengine.com/swagservice2.webp", width: 175, height: 175 },
    ],
    content: SwagModalContent,
  },
  egifting: {
    heading: "E-Gifting",
    headerImage: "https://images.changeengine.com/egiftservice-header.webp",
    minHeight: "450px",
    images: [
      { url: "https://images.changeengine.com/egiftservice1.webp" },
      { url: "https://images.changeengine.com/egiftservice3.webp" },
      { url: "https://images.changeengine.com/egiftservice2.webp" },
    ],
    content: EGiftingModalContent,
  },
  videography: {
    heading: "Videography",
    headerImage: "https://images.changeengine.com/videography.webp",
    images: [
      { url: "https://images.changeengine.com/videoservice1.webp" },
      { url: "https://images.changeengine.com/videoservice2.webp" },
      { url: "https://images.changeengine.com/videoservice3.webp" },
    ],
    content: VideographyModalContent,
  },
  designservices: {
    heading: "Design Services",
    headerImage: "https://images.changeengine.com/designservice-header.webp",
    minHeight: "600px",
    images: [
      { url: "https://images.changeengine.com/designservice1.webp" },
      { url: "https://images.changeengine.com/designservice2.webp" },
      { url: "https://images.changeengine.com/designservice3.webp", width: 180 },
    ],
    content: DesignServicesModalContent,
  },
  sms: {
    heading: "SMS",
    headerImage: "https://images.changeengine.com/sms.webp",
    minHeight: "300px",
    images: [{ url: "https://images.changeengine.com/sms3.webp", width: 480 }],
    content: SmsModalContent,
  },
  pages: {
    heading: "Pages",
    images: [
      { url: "https://images.changeengine.com/pages1.webp" },
      { url: "https://images.changeengine.com/pages2.webp" },
    ],
    content: PagesModalContent,
  },
  ats: {
    heading: "ATS",
    images: [
      { url: "https://images.changeengine.com/ats1.webp" },
      { url: "https://images.changeengine.com/ats2.webp" },
    ],
    content: AtsModalContent,
  },
  p2p: {
    heading: "Peer-to-Peer Recognition",
    images: [
      { url: "https://images.changeengine.com/p2p1.webp" },
      { url: "https://images.changeengine.com/p2p2.webp" },
    ],
    content: P2pModalContent,
  },
  sms_two_way: {
    heading: "Two-Way SMS",
    images: [
      { url: "https://images.changeengine.com/smstwoway1.webp" },
      { url: "https://images.changeengine.com/smstwoway2.webp" },
    ],
    content: TwoWaySmsModalContent,
  },
  knowledge_hub: {
    heading: "KnowledgeHub",
    images: [
      { url: "https://images.changeengine.com/knowledgehub1.webp" },
      { url: "https://images.changeengine.com/knowledgehub2.webp" },
    ],
    content: KnowledgeHubContent,
  },
  exos: {
    heading: "Exos",
    images: [
      { url: "https://images.changeengine.com/pages1.webp" },
      { url: "https://images.changeengine.com/pages2.webp" },
    ],
    content: ExosModalContent,
  },
};

export const AdditionalServicesModal: FC<{
  dialog: DialogController<{ service: AdditionalServicesType }>;
}> = ({ dialog }) => {
  const [alert, setAlert] = useState<"success" | "error" | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleRegisterInterest = useSupabaseCallback(
    async ({ supabase }) => {
      setLoading(true);
      if (!dialog.data) return;

      const { error } = await new AdditionalServicesService(supabase).insert({
        service_type: dialog.data.service,
      });

      setAlert(error ? "error" : "success");
      setLoading(false);
    },
    [dialog.data],
  );

  const handleClose = useCallback(() => {
    dialog.handleClose();
    setAlert(undefined);
  }, [dialog]);

  if (!dialog.data) return null;

  const Content = AdditionalServices[dialog.data.service].content;
  return (
    <Dialog
      sx={{
        overflow: "visible",
        "& .MuiDialog-paper": {
          overflow: "visible",
        },
      }}
      fullWidth
      maxWidth="md"
      open={dialog.open}
      onClose={handleClose}
    >
      <DialogTitle
        sx={(theme) => ({
          background: "linear-gradient(to right bottom, #00B6EF, #C24599)",
          color: "white",
          overflow: "visible",
          borderTopRightRadius: theme.shape.borderRadius,
          borderTopLeftRadius: theme.shape.borderRadius,
        })}
      >
        {AdditionalServices[dialog.data.service].heading}
        <IconButton
          data-analytics-id="additional-service-modal-close"
          sx={{
            position: "absolute",
            right: 5,
            top: 8,
            color: (theme) => theme.palette.grey[300],
            zIndex: 2,
          }}
          onClick={handleClose}
        >
          <Close data-testid="CloseIcon" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2, m: 2 }}>
        <Box sx={{ mb: 2 }}>
          {alert && (
            <Alert severity={alert}>
              {alert === "success"
                ? "Thanks for letting us know - we’ve notified your Customer Success Team and they’ll be in touch shortly."
                : "Sorry, something went wrong - please try again later or contact your Customer Success Team directly"}
            </Alert>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid size={6}>
            {<Content />}
            <Box sx={{ mt: 2 }}>
              <RegisterInterestButton
                loading={loading}
                disabled={alert === "success"}
                service={dialog.data.service}
                onClick={() => void handleRegisterInterest().catch(console.error)}
              />
            </Box>
          </Grid>
          <Grid size={6}>
            <Box
              sx={{
                mt: 2,
                minHeight: AdditionalServices[dialog.data.service].minHeight ?? "350px",
              }}
            >
              <Stack alignItems="center">
                {AdditionalServices[dialog.data.service].images.map((image, index) => (
                  <Box
                    key={index}
                    component="img"
                    sx={{
                      width: image.width ?? "250px",
                      height: image.height,
                      objectFit: "cover",
                      mt: 1,
                    }}
                    alt={dialog.data?.service}
                    src={image.url}
                  ></Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      {AdditionalServices[dialog.data.service]?.headerImage && (
        <Box
          component="img"
          sx={{
            position: "absolute",
            width: "200px",
            top: -40,
            right: 40,
            zIndex: 1,
          }}
          alt={`${dialog.data.service}-header`}
          src={AdditionalServices[dialog.data.service].headerImage}
        ></Box>
      )}
    </Dialog>
  );
};

const RegisterInterestButton: FC<{
  service: string;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}> = ({ service, disabled, loading, onClick }) => (
  <Button
    fullWidth
    onClick={onClick}
    loading={loading}
    disabled={disabled}
    variant="contained"
    data-analytics-id={`additional-services-modal-button-${service}`}
    sx={{ background: "linear-gradient(to right bottom, #00B6EF, #C24599)" }}
  >
    Let us know you’re interested
  </Button>
);
